import { defaultApiErrorHandler } from '@/services/core/ApiService'
import { handleApiError } from '@/services/core/apiResponseHandler'
import { useCartStore } from '@/stores/useCartStore'
import {
    CartNotFoundBodySchema,
    type ICart,
    type IOutStockErrorBody,
    OutStockErrorBodySchema,
} from '~/schemas/cart/cart'
import { type ApiError, hasStatusError, isApiError } from '~/schemas/errorSchemas'

/** Leitura dos erros semânticos do carrinho a partir do `ApiError` (o corpo cru fica em `error.data`). */

/**
 * 409 `cartNotFound`: devolve o carrinho que a API mandou (hoje sempre null) ou `null` se o erro é outro. Corpo
 * ilegível vale como "sem carrinho": o carrinho guardado está errado de qualquer jeito.
 */
export function getCartNotFound(error: unknown): { cart: ICart | null } | null {
    if (!hasStatusError(error, 'cartNotFound') || !isApiError(error)) return null
    const body = CartNotFoundBodySchema.safeParse(error.data)
    return { cart: body.success ? body.data.cart : null }
}

/**
 * 409 `outStockError` do create e do finish do checkout: mensagem e produtos em falta, ou `null` se o erro é outro. Lê
 * o `ApiError` dos services; qualquer outro erro passa antes pelo `handleApiError`.
 */
export function getOutOfStock(raw: unknown): IOutStockErrorBody | null {
    const error = isApiError(raw) ? raw : handleApiError(raw)
    if (!hasStatusError(error, 'outStockError')) return null
    const body = OutStockErrorBodySchema.safeParse(error.data)
    return body.success ? body.data : { statusError: 'outStockError', message: error.message, stockChanges: [] }
}

/**
 * Tratador de erro das ações do carrinho, do frete e do cupom: o toast padrão com a mensagem da API, menos no
 * `cartNotFound`, que não é erro de CEP nem de cupom: troca o carrinho guardado pelo `cart` da resposta (o aviso ao
 * cliente fica com o hook).
 */
export function cartErrorHandler(error: ApiError): void {
    const notFound = getCartNotFound(error)
    if (notFound) {
        useCartStore.getState().setCart(notFound.cart)
        return
    }
    defaultApiErrorHandler(error)
}
